import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import CreateFOCustomerForm from '../components/CreateFOCustomerForm';
import EditFOCustomerModal from '../components/EditFOCustomerModal';
import axios from 'axios';
import { FiPlus, FiEye, FiEdit2, FiTrash2, FiSearch, FiX } from 'react-icons/fi';

const FOCustomerPage = () => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [customerOrders, setCustomerOrders] = useState([]);
    const [loadingCustomerOrders, setLoadingCustomerOrders] = useState(false);

    const fetchCustomers = async (page, search = '') => {
        try {
            setLoading(true);
            let apiUrl = `${process.env.REACT_APP_API_URL}/api/focustomers?page=${page}&limit=20`;
            
            if (search) apiUrl += `&search=${search}`;
            
            const token = localStorage.getItem('token');
            const response = await axios.get(
                apiUrl,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'ngrok-skip-browser-warning': 'true'
                    }
                }
            );
            
            if (response.data.success) {
                setCustomers(response.data.data);
                setTotalPages(response.data.pagination.pages);
                setTotalCustomers(response.data.pagination.total);
            }
        } catch (err) {
            setError(`Failed to fetch GZ customers: ${err.response?.data?.error || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCustomers(currentPage, searchTerm);
    }, [currentPage, searchTerm]);

    const handleCreateSuccess = (newCustomer) => {
        setShowCreateForm(false);
        fetchCustomers(currentPage, searchTerm);
    };

    const handleEditSuccess = () => {
        setShowEditModal(false);
        setSelectedCustomer(null);
        fetchCustomers(currentPage, searchTerm);
    };

    const handleDelete = (customer) => {
        setSelectedCustomer(customer);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/focustomers/${selectedCustomer._id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            setShowDeleteConfirm(false);
            setSelectedCustomer(null);
            fetchCustomers(currentPage, searchTerm);
        } catch (err) {
            setError(`Failed to delete GZ customer: ${err.response?.data?.error || err.message}`);
        }
    };

    const handleView = (customer) => {
        setSelectedCustomer(customer);
        setCustomerOrders([]);
        setShowViewModal(true);
    };

    // Fetch orders for selected customer when view modal is open
    useEffect(() => {
        if (!showViewModal || !selectedCustomer?._id) {
            setCustomerOrders([]);
            return;
        }
        const fetchCustomerOrders = async () => {
            setLoadingCustomerOrders(true);
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/api/foorders/customer/${selectedCustomer._id}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'ngrok-skip-browser-warning': 'true'
                        }
                    }
                );
                if (response.data.success) {
                    setCustomerOrders(response.data.data || []);
                }
            } catch (err) {
                console.error('Failed to fetch customer orders:', err);
                setCustomerOrders([]);
            } finally {
                setLoadingCustomerOrders(false);
            }
        };
        fetchCustomerOrders();
    }, [showViewModal, selectedCustomer?._id]);

    const handleEdit = (customer) => {
        setSelectedCustomer(customer);
        setShowEditModal(true);
    };

    const handleSearch = () => {
        setSearchTerm(searchInput);
        setCurrentPage(1);
    };

    const handleClear = () => {
        setSearchInput('');
        setSearchTerm('');
        setCurrentPage(1);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-SG', { year: 'numeric', month: '2-digit' });
    };
    const formatCreateMonthYear = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-SG', { year: 'numeric', month: '2-digit' });
    };

    const formatNumber = (value, decimals = 2) => {
        if (!value && value !== 0) return 'N/A';
        return Number(value).toFixed(decimals);
    };

    const formatSGD = (value) => {
        if (value == null || value === '') return 'N/A';
        return `$${Number(value).toFixed(2)}`;
    };

    // Earliest order date among all items in the order
    const getEarliestOrderDate = (order) => {
        if (!order?.items?.length) return null;
        const dates = order.items.map(i => i.orderDate).filter(Boolean);
        if (dates.length === 0) return null;
        return new Date(Math.min(...dates.map(d => new Date(d))));
    };

    // Orders sorted by earliest order date descending (newest first)
    const sortedCustomerOrders = [...customerOrders].map(order => ({
        ...order,
        _earliestOrderDate: getEarliestOrderDate(order)
    })).sort((a, b) => {
        if (!a._earliestOrderDate) return 1;
        if (!b._earliestOrderDate) return -1;
        return b._earliestOrderDate - a._earliestOrderDate;
    });

    // Format Telegram for table: "username - tgNumber" per entry; multiple joined by ", "
    const formatTelegramForTable = (telegramArr) => {
        if (!telegramArr || !telegramArr.length) return 'N/A';
        return telegramArr.map(t => {
            if (typeof t === 'string') return t;
            const u = t?.username ?? '';
            const n = t?.tgNumber ?? '';
            return [u, n].filter(Boolean).join(' - ') || '—';
        }).filter(Boolean).join(', ');
    };

    // Measurement: "yes" if any of height/weight/waist/wrist/shoeSize/ringSize is set
    const hasMeasurement = (c) => {
        const v = (x) => x != null && x !== '';
        return v(c.Height) || v(c.Weight) || v(c.waist) || v(c.wrist) || v(c.shoeSize) || v(c.ringSize);
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">GZ Customers</h1>
                        <p className="text-gray-600">Total: {totalCustomers} customers</p>
                    </div>
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                    >
                        <FiPlus className="mr-2" />
                        Create New Customer
                    </button>
                </div>

                {/* Search Bar */}
                <div className="mb-6">
                    <div className="flex gap-2">
                        <div className="relative flex-1">
                            <input
                                type="text"
                                className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-blue-700 font-bold"
                                placeholder="Search by Customer ID, Name, Email, Address, Phone, WhatsApp, or Telegram..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                            />
                            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                <FiSearch className="h-5 w-5 text-gray-400" />
                            </div>
                        </div>
                        <button
                            onClick={handleSearch}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                        >
                            <FiSearch className="mr-2" />
                            Search
                        </button>
                        <button
                            onClick={handleClear}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center"
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">
                        {error}
                    </div>
                )}

                {/* Create Form Modal */}
                {showCreateForm && (
                    <CreateFOCustomerForm
                        onSuccess={handleCreateSuccess}
                        onCancel={() => setShowCreateForm(false)}
                    />
                )}

                {/* Edit Customer Modal */}
                {showEditModal && selectedCustomer && (
                    <EditFOCustomerModal
                        customer={selectedCustomer}
                        onSuccess={handleEditSuccess}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedCustomer(null);
                        }}
                    />
                )}

                {/* View Modal - FO Customer Details */}
                {showViewModal && selectedCustomer && (
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                        <div className="relative top-20 mx-auto p-5 border max-w-4xl w-full shadow-lg rounded-md bg-white">
                            {/* Title: Left = FO Customer Details - CustomerID - Name ; Right = Create Month/Year (blue bold) */}
                            <div className="flex justify-between items-center border-b pb-4 mb-4">
                                <h3 className="text-xl font-medium text-left">
                                    GZ Customer Details - <span className="text-blue-700 font-bold">{selectedCustomer.customerID || ''} - {selectedCustomer.Name || ''}</span>
                                </h3>
                                <div className="flex items-center gap-2">
                                    <span className="text-blue-700 font-bold text-sm">{formatCreateMonthYear(selectedCustomer.createMMYY) || 'N/A'}</span>
                                    <button
                                        onClick={() => { setShowViewModal(false); setSelectedCustomer(null); setCustomerOrders([]); }}
                                        className="text-gray-500 hover:text-gray-700 ml-2"
                                        aria-label="Close modal"
                                    >
                                        <FiX className="w-6 h-6" />
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-6">
                                {/* First Card: Contact Information */}
                                <section>
                                    <h4 className="text-lg font-medium mb-3">Contact Information</h4>
                                    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                                        {/* Row 1: Email, Phone, Whatsapp (no value → N/A) */}
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                            <div>
                                                <span className="text-gray-600 text-sm font-semibold">Email</span>
                                                <div className="mt-0.5 text-blue-700 text-sm">
                                                    {(selectedCustomer.email && selectedCustomer.email.length > 0)
                                                        ? selectedCustomer.email.map((email, index) => (
                                                            <div key={index}>{selectedCustomer.email.length > 1 ? `${index + 1}. ${email}` : email}</div>
                                                        ))
                                                        : 'N/A'}
                                                </div>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 text-sm font-semibold">Phone</span>
                                                <div className="mt-0.5 text-blue-700 text-sm">
                                                    {(selectedCustomer.Phone && selectedCustomer.Phone.length > 0)
                                                        ? selectedCustomer.Phone.map((phone, index) => (
                                                            <div key={index}>{selectedCustomer.Phone.length > 1 ? `${index + 1}. ${phone}` : phone}</div>
                                                        ))
                                                        : 'N/A'}
                                                </div>
                                            </div>
                                            <div>
                                                <span className="text-gray-600 text-sm font-semibold">Whatsapp</span>
                                                <div className="mt-0.5 text-blue-700 text-sm">
                                                    {(selectedCustomer.Whatsapp && selectedCustomer.Whatsapp.length > 0)
                                                        ? selectedCustomer.Whatsapp.map((wa, index) => (
                                                            <div key={index}>{selectedCustomer.Whatsapp.length > 1 ? `${index + 1}. ${wa}` : wa}</div>
                                                        ))
                                                        : 'N/A'}
                                                </div>
                                            </div>
                                        </div>
                                        {/* Row 2: Telegram - tgUsername - tgNumber */}
                                        <div>
                                            <span className="text-gray-600 text-sm font-semibold">Telegram</span>
                                            <div className="mt-0.5 text-blue-700 text-sm">
                                                {(selectedCustomer.Telegram && selectedCustomer.Telegram.length > 0)
                                                    ? selectedCustomer.Telegram.map((tg, index) => {
                                                        const display = typeof tg === 'string' ? tg : `${tg?.username || ''} - ${tg?.tgNumber || ''}`.replace(/^\s*-\s*|-\s*$/g, '').trim() || '';
                                                        return (
                                                            <div key={index}>
                                                                {selectedCustomer.Telegram.length > 1 ? `${index + 1}. ${display || 'N/A'}` : (display || 'N/A')}
                                                            </div>
                                                        );
                                                    })
                                                    : 'N/A'}
                                            </div>
                                        </div>
                                        {/* Next row: Address and value */}
                                        <div>
                                            <span className="text-gray-600 text-sm font-semibold">Address</span>
                                            <div className="mt-0.5 text-blue-700 text-sm">
                                                {(selectedCustomer.Address && selectedCustomer.Address.length > 0)
                                                    ? selectedCustomer.Address.map((addr, index) => (
                                                        <div key={index}>{selectedCustomer.Address.length > 1 ? `${index + 1}. ${addr}` : addr}</div>
                                                    ))
                                                    : 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {/* Measurements Card: label and value all in one row */}
                                <section>
                                    <h4 className="text-lg font-medium mb-3">Measurements</h4>
                                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                                        <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
                                            <span className="text-gray-700">Height: <span className="text-blue-700">{selectedCustomer.Height != null && selectedCustomer.Height !== '' ? `${formatNumber(selectedCustomer.Height, 0)} cm` : 'N/A'}</span></span>
                                            <span className="text-gray-700">Weight: <span className="text-blue-700">{selectedCustomer.Weight != null && selectedCustomer.Weight !== '' ? `${formatNumber(selectedCustomer.Weight, 2)} kg` : 'N/A'}</span></span>
                                            <span className="text-gray-700">Waist: <span className="text-blue-700">{selectedCustomer.waist != null && selectedCustomer.waist !== '' ? `${formatNumber(selectedCustomer.waist, 2)} cm` : 'N/A'}</span></span>
                                            <span className="text-gray-700">Wrist: <span className="text-blue-700">{selectedCustomer.wrist != null && selectedCustomer.wrist !== '' ? `${formatNumber(selectedCustomer.wrist, 2)} cm` : 'N/A'}</span></span>
                                            <span className="text-gray-700">Shoe Size: <span className="text-blue-700">{selectedCustomer.shoeSize != null && selectedCustomer.shoeSize !== '' ? formatNumber(selectedCustomer.shoeSize, 2) : 'N/A'}</span></span>
                                            <span className="text-gray-700">Ring Size: <span className="text-blue-700">{selectedCustomer.ringSize != null && selectedCustomer.ringSize !== '' ? formatNumber(selectedCustomer.ringSize, 2) : 'N/A'}</span></span>
                                        </div>
                                    </div>
                                </section>

                                {/* Remarks Card: value only */}
                                <section>
                                    <h4 className="text-lg font-medium mb-3">Remarks</h4>
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <p className="text-blue-700 text-sm whitespace-pre-line">{selectedCustomer.remarks || 'N/A'}</p>
                                    </div>
                                </section>

                                {/* Order: Total Order, Total Revenue, Order list table */}
                                <section>
                                    <h4 className="text-lg font-medium mb-3">Order</h4>
                                    <div className="bg-gray-50 p-4 rounded-lg space-y-3">
                                        <div className="flex flex-wrap gap-6 text-sm">
                                            <span className="text-gray-700">Total Order: <span className="font-semibold text-blue-700">{selectedCustomer.orderCount ?? customerOrders.length}</span></span>
                                            <span className="text-gray-700">Total Revenue: <span className="font-semibold text-blue-700">{formatSGD(selectedCustomer.totalRevenue ?? 0)}</span></span>
                                        </div>
                                        <div className="overflow-x-auto mt-3">
                                            {loadingCustomerOrders ? (
                                                <div className="py-4 text-center text-gray-500 text-sm">Loading orders...</div>
                                            ) : sortedCustomerOrders.length === 0 ? (
                                                <p className="text-gray-500 text-sm py-2">No orders</p>
                                            ) : (
                                                <table className="min-w-full text-sm border border-gray-200 rounded-md overflow-hidden">
                                                    <thead className="bg-gray-100">
                                                        <tr>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">#</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Purchase Code</th>
                                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Order Date</th>
                                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Item Count</th>
                                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Total Sales</th>
                                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Total Profit</th>
                                                            <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Margin</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {sortedCustomerOrders.map((order, index) => (
                                                            <tr key={order._id} className="hover:bg-gray-50">
                                                                <td className="px-3 py-2 text-gray-700">{index + 1}</td>
                                                                <td className="px-3 py-2 text-gray-700">{order.purchaseCode ?? '—'}</td>
                                                                <td className="px-3 py-2 text-gray-700">
                                                                    {order._earliestOrderDate ? order._earliestOrderDate.toLocaleDateString('en-SG', { day: '2-digit', month: '2-digit', year: 'numeric' }) : 'N/A'}
                                                                </td>
                                                                <td className="px-3 py-2 text-right text-gray-700">{order.items?.length ?? 0}</td>
                                                                <td className="px-3 py-2 text-right text-gray-700">{formatSGD(order.totalSales)}</td>
                                                                <td className="px-3 py-2 text-right text-green-600 font-medium">{formatSGD(order.totalProfit)}</td>
                                                                <td className="px-3 py-2 text-right text-gray-700">{order.margin != null ? `${formatNumber(order.margin)}%` : 'N/A'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>
                )}

                {/* Delete Confirmation Modal */}
                {showDeleteConfirm && selectedCustomer && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 w-full max-w-md">
                            <h2 className="text-xl font-bold mb-4">Confirm Delete</h2>
                            <p className="mb-6">Are you sure you want to delete "{selectedCustomer.Name || selectedCustomer.customerID}"?</p>
                            <div className="flex justify-end gap-2">
                                <button
                                    onClick={() => { setShowDeleteConfirm(false); setSelectedCustomer(null); }}
                                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Table */}
                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Customer
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    WhatsApp
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Telegram
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Measurement
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Total Order
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Total Revenue
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Remarks
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center">
                                        Loading...
                                    </td>
                                </tr>
                            ) : customers.length === 0 ? (
                                <tr>
                                    <td colSpan="8" className="px-6 py-4 text-center">
                                        No GZ customers found
                                    </td>
                                </tr>
                            ) : (
                                customers.map((customer) => (
                                    <tr key={customer._id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            {`${customer.customerID || ''} - ${customer.Name || ''}`}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {customer.Whatsapp?.length ? customer.Whatsapp.join(', ') : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 text-sm">
                                            {formatTelegramForTable(customer.Telegram)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {hasMeasurement(customer) ? '✅' : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {customer.orderCount ?? 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            {customer.totalRevenue != null ? `$${Number(customer.totalRevenue).toFixed(2)}` : '$0.00'}
                                        </td>
                                        <td className="px-6 py-4 text-sm max-w-xs truncate" title={customer.remarks || ''}>
                                            {customer.remarks || 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                                            <button
                                                onClick={() => handleView(customer)}
                                                className="p-1.5 text-blue-600 hover:text-blue-900 hover:bg-blue-100 rounded-full inline-flex items-center"
                                                title="View Details"
                                            >
                                                <FiEye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleEdit(customer)}
                                                className="p-1.5 text-green-600 hover:text-green-900 hover:bg-green-100 rounded-full inline-flex items-center"
                                                title="Edit Customer"
                                            >
                                                <FiEdit2 className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(customer)}
                                                className="p-1.5 text-red-600 hover:text-red-900 hover:bg-red-100 rounded-full inline-flex items-center"
                                                title="Delete Customer"
                                            >
                                                <FiTrash2 className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                <Pagination 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </div>
        </Layout>
    );
};

export default FOCustomerPage;
